import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  enviarPedidoCompraPublico,
  getPedidoCompraPublico,
  unlockPedidoCompraPublico,
  updatePedidoCompraPublicoItens,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

const money = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const num = (v) => {
  const n = Number(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const fmtQtd = (n) => {
  const x = Number(n) || 0;
  return Number.isInteger(x) ? String(x) : x.toFixed(3);
};

const fmtDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR');
};

const storageKey = (token) => `zen.pc.access.${token}`;

const lerAccessToken = (token) => {
  try {
    return sessionStorage.getItem(storageKey(token)) || '';
  } catch {
    return '';
  }
};

const gravarAccessToken = (token, value) => {
  try {
    if (value) sessionStorage.setItem(storageKey(token), value);
    else sessionStorage.removeItem(storageKey(token));
  } catch {
    // sessionStorage indisponível: segue só em memória
  }
};

const mapItens = (itens) =>
  (itens || []).map((i) => ({
    id: i.id,
    produtoCodigo: i.produtoCodigo,
    produtoDescricao: i.produtoDescricao,
    qtdPedida: Number(i.qtdPedida || 0),
    observacao: i.observacao || '',
    custoUnitario: Number(i.custoUnitario || 0),
    // Sugere o pedido inteiro quando o fornecedor ainda não respondeu.
    qtdAtendida: Number(i.qtdAtendida) > 0 ? Number(i.qtdAtendida) : Number(i.qtdPedida || 0),
  }));

export default function FornecedorPedidoCompraPage() {
  const { slug, token } = useParams();
  const [accessToken, setAccessToken] = useState(() => lerAccessToken(token));
  const [codigoAcesso, setCodigoAcesso] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [loading, setLoading] = useState(Boolean(lerAccessToken(token)));
  const [saving, setSaving] = useState(false);
  const [doc, setDoc] = useState(null);
  const [itens, setItens] = useState([]);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const sair = useCallback((mensagem) => {
    gravarAccessToken(token, '');
    setAccessToken('');
    setDoc(null);
    setItens([]);
    setLoading(false);
    if (mensagem) setError(mensagem);
  }, [token]);

  const carregar = useCallback(async (at) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await getPedidoCompraPublico(slug, token, at);
      setDoc(data);
      setItens(mapItens(data.itens));
    } catch (e) {
      const st = e?.response?.status;
      if (st === 401) sair('Sessão expirada. Informe o código de acesso novamente.');
      else {
        sair('');
        setError(getApiErrorMessage(e, 'Não foi possível carregar o pedido.'));
      }
    } finally {
      setLoading(false);
    }
  }, [slug, token, sair]);

  useEffect(() => {
    if (accessToken) carregar(accessToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const desbloquear = async (e) => {
    e?.preventDefault();
    const codigo = codigoAcesso.trim();
    if (codigo.length < 4) {
      setError('Informe o código de acesso.');
      return;
    }
    setUnlocking(true);
    setError('');
    try {
      const { data } = await unlockPedidoCompraPublico(slug, token, codigo);
      gravarAccessToken(token, data.accessToken);
      setAccessToken(data.accessToken);
      setCodigoAcesso('');
      await carregar(data.accessToken);
    } catch (err) {
      const st = err?.response?.status;
      setError(
        st === 401
          ? 'Código de acesso inválido.'
          : getApiErrorMessage(err, 'Não foi possível validar o código de acesso.'),
      );
    } finally {
      setUnlocking(false);
    }
  };

  const editavel = doc?.status === 'Enviado';

  const total = itens.reduce((s, i) => {
    const base = num(i.qtdAtendida) > 0 ? num(i.qtdAtendida) : num(i.qtdPedida);
    return s + base * num(i.custoUnitario);
  }, 0);

  const setField = (idx, patch) =>
    setItens((prev) => prev.map((i, k) => (k === idx ? { ...i, ...patch } : i)));

  const validar = () => {
    if (itens.some((i) => num(i.custoUnitario) < 0 || num(i.qtdAtendida) < 0)) {
      setError('Custo e quantidade não podem ser negativos.');
      return false;
    }
    return true;
  };

  const salvarItens = async () => {
    const { data } = await updatePedidoCompraPublicoItens(
      slug,
      token,
      accessToken,
      itens.map((i) => ({
        id: i.id,
        custoUnitario: num(i.custoUnitario),
        qtdAtendida: num(i.qtdAtendida),
      })),
    );
    setDoc(data);
    setItens(mapItens(data.itens));
  };

  const tratarErro = (e, fallback) => {
    if (e?.response?.status === 401) {
      sair('Sessão expirada. Informe o código de acesso novamente.');
      return;
    }
    setError(getApiErrorMessage(e, fallback));
  };

  const handleSalvar = async () => {
    setError('');
    setInfo('');
    if (!validar()) return;
    setSaving(true);
    try {
      await salvarItens();
      setInfo('Alterações salvas.');
    } catch (e) {
      tratarErro(e, 'Não foi possível salvar as alterações.');
    } finally {
      setSaving(false);
    }
  };

  const handleEnviar = async () => {
    setError('');
    setInfo('');
    if (!validar()) return;
    if (!window.confirm(
      'Enviar a resposta ao comprador? Depois de enviada, não será possível alterar.',
    )) return;
    setSaving(true);
    try {
      await salvarItens();
      const { data } = await enviarPedidoCompraPublico(slug, token, accessToken);
      setDoc(data);
      setItens(mapItens(data.itens));
      setInfo('Resposta enviada com sucesso. Obrigado!');
    } catch (e) {
      tratarErro(e, 'Não foi possível enviar a resposta.');
    } finally {
      setSaving(false);
    }
  };

  const conteudoLogin = (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <form onSubmit={desbloquear}>
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={800}>Acesso ao pedido de compra</Typography>
          <Typography variant="body2" color="text.secondary">
            Informe o código de acesso que o comprador enviou junto com este link.
          </Typography>
          <TextField
            label="Código de acesso"
            value={codigoAcesso}
            onChange={(e) => setCodigoAcesso(e.target.value)}
            autoFocus
            fullWidth
            inputProps={{ maxLength: 12, style: { letterSpacing: 4, fontWeight: 700 } }}
          />
          <Button type="submit" variant="contained" disabled={unlocking}>
            {unlocking ? 'Validando…' : 'Acessar'}
          </Button>
        </Stack>
      </form>
    </Paper>
  );

  const conteudoPedido = doc && (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          spacing={1}
        >
          <Box>
            <Typography variant="h5" fontWeight={900}>Pedido {doc.codigo}</Typography>
            <Typography color="text.secondary">
              Solicitante: <strong>{doc.estabelecimentoNome || '—'}</strong>
              {' · '}Fornecedor: <strong>{doc.fornecedorNome || '—'}</strong>
            </Typography>
            {doc.linkExpiraEm && (
              <Typography variant="caption" color="text.secondary">
                Link válido até {fmtDate(doc.linkExpiraEm)}
              </Typography>
            )}
          </Box>
          <Chip
            label={doc.status}
            color={doc.status === 'Enviado' ? 'info' : 'success'}
          />
        </Stack>
        {doc.observacao ? (
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            Observação do comprador: {doc.observacao}
          </Typography>
        ) : null}
      </Paper>

      {editavel ? (
        <Alert severity="info">
          Informe o custo unitário e a quantidade que você consegue atender em cada item,
          salve e depois envie a resposta.
        </Alert>
      ) : (
        <Alert severity={doc.status === 'Respondido' ? 'success' : 'warning'}>
          {doc.status === 'Respondido'
            ? 'Resposta já enviada. O pedido agora está somente para consulta.'
            : `Pedido com status ${doc.status}: somente para consulta.`}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Produto</TableCell>
              <TableCell align="right">Qtd pedida</TableCell>
              <TableCell align="right">Qtd atendida</TableCell>
              <TableCell align="right">Custo unit.</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {itens.map((i, idx) => {
              const base = num(i.qtdAtendida) > 0 ? num(i.qtdAtendida) : num(i.qtdPedida);
              return (
                <TableRow key={i.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>{i.produtoDescricao}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {i.produtoCodigo}{i.observacao ? ` · ${i.observacao}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{fmtQtd(i.qtdPedida)}</TableCell>
                  <TableCell align="right">
                    {editavel ? (
                      <TextField
                        size="small"
                        type="number"
                        value={i.qtdAtendida}
                        onChange={(e) => setField(idx, { qtdAtendida: e.target.value })}
                        inputProps={{ min: 0, step: 'any' }}
                        sx={{ width: 110 }}
                      />
                    ) : fmtQtd(i.qtdAtendida)}
                  </TableCell>
                  <TableCell align="right">
                    {editavel ? (
                      <TextField
                        size="small"
                        type="number"
                        value={i.custoUnitario}
                        onChange={(e) => setField(idx, { custoUnitario: e.target.value })}
                        inputProps={{ min: 0, step: 'any' }}
                        sx={{ width: 120 }}
                      />
                    ) : money(i.custoUnitario)}
                  </TableCell>
                  <TableCell align="right">{money(base * num(i.custoUnitario))}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Typography align="right" variant="h6" fontWeight={800}>
        Total: {money(editavel ? total : doc.valorTotal)}
      </Typography>

      {editavel && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
          <Button variant="outlined" disabled={saving} onClick={handleSalvar}>
            {saving ? 'Salvando…' : 'Salvar alterações'}
          </Button>
          <Button variant="contained" disabled={saving} onClick={handleEnviar}>
            Enviar resposta
          </Button>
        </Stack>
      )}
    </Stack>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 2, md: 5 } }}>
      <Container maxWidth="md">
        <Typography variant="overline" color="text.secondary">Portal do fornecedor</Typography>
        {error && (
          <Alert severity="error" sx={{ my: 2 }} onClose={() => setError('')}>{error}</Alert>
        )}
        {info && (
          <Alert severity="success" sx={{ my: 2 }} onClose={() => setInfo('')}>{info}</Alert>
        )}
        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
        ) : accessToken && doc ? (
          conteudoPedido
        ) : (
          conteudoLogin
        )}
      </Container>
    </Box>
  );
}
